import { useContext, useState, useEffect, useCallback } from 'react';
import { Check, X, Clock, CheckCircle2, XCircle, Loader2, FileText, ChevronDown } from 'lucide-react';
import { GlobalContext } from '../../context/GlobalContext';
import { unsafeAPI } from '../../hooks/api';
import { Button } from '../../shadcn/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../shadcn/Dialog';
import Notice from '../../components/Notice';
import Input from '../../components/Input';
import { ContainerSpinner } from '../../components/Spinner';

const PAGE_SIZE = 100;

type BankInfo = {
	bank_name: string;
	account_name: string;
	account_number: string;
	account_type: string;
	document_type?: string;
	identification_number?: string;
	interbank_code?: string;
};

type StoreRequest = {
	id: string;
	store_email: string;
	requested_by: string;
	request_type: 'bank_change';
	status: 'pending' | 'approved' | 'denied' | 'info';
	payload: Record<string, any>;
	created_at: string;
	reviewed_at?: string;
	reviewed_by?: string;
	notes?: string;
};

const STATUS_TABS = [
	{ id: 'pending', label: 'Pendientes', icon: Clock },
	{ id: 'approved', label: 'Aprobadas', icon: CheckCircle2 },
	{ id: 'denied', label: 'Rechazadas', icon: XCircle }
] as const;

const REQUEST_TYPE_LABELS: Record<string, string> = {
	bank_change: 'Cambio bancario'
};

export default function StoreRequests() {
	const { userData, internalCountry } = useContext(GlobalContext);
	const [activeTab, setActiveTab] = useState<string>('pending');
	const typeFilter = 'bank_change';
	const [requests, setRequests] = useState<StoreRequest[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [notice, setNotice] = useState<{
		message: string;
		variant: 'success' | 'error';
	} | null>(null);
	const [actionLoading, setActionLoading] = useState<string | null>(null);
	const [notesMap, setNotesMap] = useState<Record<string, string>>({});

	const showNotice = (message: string, variant: 'success' | 'error') => {
		setNotice({ message, variant });
		setTimeout(() => setNotice(null), 3000);
	};

	const fetchRequests = useCallback(
		async (status: string, requestType: string, cursor?: string | null) => {
			const isLoadMore = !!cursor;
			if (isLoadMore) setLoadingMore(true);
			else setLoading(true);
			try {
				const params = new URLSearchParams({
					status,
					page_size: String(PAGE_SIZE)
				});
				if (requestType) params.set('request_type', requestType);
				if (cursor) params.set('cursor', cursor);
				const res = await unsafeAPI(
					'GET',
					`https://api.krece.app/storeprofile/perfil/store-requests/?${params.toString()}`,
					null,
					userData,
					internalCountry.get()
				);
				if (res?.success) {
					const newData = res.data || [];
					setRequests((prev) => (isLoadMore ? [...prev, ...newData] : newData));
					setNextCursor(res.next_cursor || null);
				} else {
					if (!isLoadMore) setRequests([]);
					showNotice(res?.status || 'Error cargando solicitudes', 'error');
				}
			} catch {
				if (!isLoadMore) setRequests([]);
				showNotice('Error cargando solicitudes', 'error');
			} finally {
				setLoading(false);
				setLoadingMore(false);
			}
		},
		[userData, internalCountry]
	);

	useEffect(() => {
		setNextCursor(null);
		fetchRequests(activeTab, typeFilter);
	}, [activeTab, fetchRequests]);

	const loadMore = () => {
		if (nextCursor && !loadingMore) {
			fetchRequests(activeTab, typeFilter, nextCursor);
		}
	};

	const handleAction = async (requestId: string, action: 'approved' | 'denied') => {
		setActionLoading(requestId);
		try {
			const res = await unsafeAPI(
				'PATCH',
				'https://api.krece.app/storeprofile/perfil/store-requests/',
				{
					request_id: requestId,
					status: action,
					notes: notesMap[requestId] || ''
				},
				userData,
				internalCountry.get()
			);
			if (res?.success) {
				showNotice(`Solicitud ${action === 'approved' ? 'aprobada' : 'rechazada'} exitosamente`, 'success');
				setRequests((prev) => prev.filter((r) => r.id !== requestId));
			} else {
				showNotice(res?.status || 'Error procesando solicitud', 'error');
			}
		} catch {
			showNotice('Error procesando solicitud', 'error');
		} finally {
			setActionLoading(null);
		}
	};

	if (!userData) return <ContainerSpinner />;

	const statusLabel =
		activeTab === 'pending'
			? 'pendientes'
			: activeTab === 'info'
				? 'informativas'
				: activeTab === 'approved'
					? 'aprobadas'
					: 'rechazadas';

	return (
		<div className="flex h-full w-full flex-col gap-4 overflow-hidden p-4 md:p-6">
			<h1 className="shrink-0 text-lg font-semibold text-neutral-100">Solicitudes de tiendas</h1>

			{/* Filters row */}
			<div className="flex shrink-0 items-center gap-4">
				{/* Status tabs */}
				<div className="flex gap-1">
					{STATUS_TABS.map((tab) => {
						const Icon = tab.icon;
						const isActive = activeTab === tab.id;
						return (
							<button
								key={tab.id}
								onClick={() => setActiveTab(tab.id)}
								className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-all ${
									isActive ? 'bg-neutral-800 text-neutral-100' : 'text-neutral-500 hover:bg-neutral-800/50 hover:text-neutral-300'
								}`}
							>
								<Icon className="size-3.5" />
								{tab.label}
							</button>
						);
					})}
				</div>
			</div>

			{notice && <Notice message={notice.message} variant={notice.variant} />}

			{/* Content */}
			<div className="min-h-0 flex-1 overflow-y-auto">
				{loading ? (
					<ContainerSpinner />
				) : requests.length === 0 ? (
					<div className="flex flex-col items-center justify-center gap-2 py-16 text-neutral-500">
						<FileText className="size-8" />
						<p className="text-sm">No hay solicitudes {statusLabel}</p>
					</div>
				) : (
					<div className="flex flex-col gap-4">
						{requests.map((req) =>
							req.request_type === 'bank_change' ? (
								<BankChangeCard
									key={req.id}
									request={req}
									isPending={req.status === 'pending'}
									notes={notesMap[req.id] || ''}
									onNotesChange={(val) => setNotesMap((prev) => ({ ...prev, [req.id]: val }))}
									onApprove={() => handleAction(req.id, 'approved')}
									onDeny={() => handleAction(req.id, 'denied')}
									loading={actionLoading === req.id}
								/>
							) : null
						)}
						{nextCursor && (
							<button
								onClick={loadMore}
								disabled={loadingMore}
								className="mx-auto flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-neutral-400 transition-colors hover:bg-neutral-800/50 hover:text-neutral-200"
							>
								{loadingMore ? <Loader2 className="size-4 animate-spin" /> : <ChevronDown className="size-4" />}
								Cargar más
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

function RequestHeader({ request, showReviewer }: { request: StoreRequest; showReviewer: boolean }) {
	const createdDate = request.created_at
		? new Date(request.created_at).toLocaleDateString('es', {
				day: 'numeric',
				month: 'short',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			})
		: '\u2014';

	return (
		<div className="mb-4 flex items-start justify-between gap-4">
			<div className="flex items-center gap-3">
				<div>
					<div className="flex items-center gap-2">
						<p className="text-sm font-medium text-neutral-100">{request.store_email}</p>
						<span className="rounded-full bg-neutral-800 px-2 py-0.5 text-[10px] font-medium text-neutral-400">
							{REQUEST_TYPE_LABELS[request.request_type] || request.request_type}
						</span>
					</div>
					<p className="mt-0.5 text-xs text-neutral-500">
						Solicitado por {request.requested_by} · {createdDate}
					</p>
				</div>
			</div>
			{showReviewer && request.reviewed_by && (
				<div className="text-right">
					<p className="text-[10px] uppercase tracking-wider text-neutral-500">Revisado por</p>
					<p className="text-xs text-neutral-400">{request.reviewed_by}</p>
				</div>
			)}
		</div>
	);
}

function BankInfoColumn({ label, info }: { label: string; info: BankInfo }) {
	const fields = [
		{ key: 'Banco', value: info.bank_name },
		{ key: 'Titular', value: info.account_name },
		{
			key: 'Cuenta',
			value: `${info.account_type || ''} ${info.account_number || ''}`.trim()
		},
		{
			key: 'Documento',
			value: info.document_type && info.identification_number ? `${info.document_type} ${info.identification_number}` : ''
		},
		{ key: 'Interbancario', value: info.interbank_code || '' }
	].filter((f) => f.value);

	return (
		<div className="flex-1">
			<p className="mb-2 text-[11px] font-medium uppercase tracking-widest text-neutral-500">{label}</p>
			<div className="space-y-1.5">
				{fields.map((f) => (
					<div key={f.key} className="rounded-md bg-neutral-900/50 px-2.5 py-1.5">
						<p className="text-[10px] text-neutral-500">{f.key}</p>
						<p className="text-xs font-medium text-neutral-200">{f.value}</p>
					</div>
				))}
			</div>
		</div>
	);
}

function BankChangeCard({
	request,
	isPending,
	notes,
	onNotesChange,
	onApprove,
	onDeny,
	loading
}: {
	request: StoreRequest;
	isPending: boolean;
	notes: string;
	onNotesChange: (val: string) => void;
	onApprove: () => void;
	onDeny: () => void;
	loading: boolean;
}) {
	const currentBank = (request.payload?.current_bank_info || {}) as BankInfo;
	const newBank = (request.payload?.new_bank_info || {}) as BankInfo;
	const [confirmAction, setConfirmAction] = useState<'approved' | 'denied' | null>(null);

	return (
		<>
			<div className="rounded-xl bg-gradient-to-br from-neutral-900/60 to-neutral-900/20 p-5 shadow-lg backdrop-blur-sm">
				<div className="flex items-start justify-between gap-4">
					<div className="flex-1">
						<RequestHeader request={request} showReviewer={!isPending} />
					</div>
					{isPending && (
						<div className="flex shrink-0 items-center gap-1">
							{loading ? (
								<Loader2 className="size-4 animate-spin text-neutral-500" />
							) : (
								<>
									<button
										onClick={() => setConfirmAction('denied')}
										className="rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-red-500/10 hover:text-red-400"
										title="Rechazar"
									>
										<X className="size-4" />
									</button>
									<button
										onClick={() => setConfirmAction('approved')}
										className="rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-emerald-500/10 hover:text-emerald-400"
										title="Aprobar"
									>
										<Check className="size-4" />
									</button>
								</>
							)}
						</div>
					)}
				</div>

				{/* Bank info comparison */}
				<div className="flex gap-4">
					<BankInfoColumn label="Cuenta actual" info={currentBank} />
					<div className="flex items-center text-neutral-600">&rarr;</div>
					<BankInfoColumn label="Cuenta nueva" info={newBank} />
				</div>

				{/* Notes from reviewer (for approved/denied) */}
				{!isPending && request.notes && (
					<div className="mt-3 rounded-md bg-neutral-900/50 px-3 py-2">
						<p className="text-[10px] uppercase tracking-wider text-neutral-500">Notas</p>
						<p className="text-xs text-neutral-300">{request.notes}</p>
					</div>
				)}
			</div>

			{/* Confirmation dialog */}
			<Dialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{confirmAction === 'approved' ? 'Aprobar solicitud' : 'Rechazar solicitud'}</DialogTitle>
						<DialogDescription>
							{confirmAction === 'approved'
								? `¿Está seguro que desea aprobar el cambio bancario de ${request.store_email}?`
								: `¿Está seguro que desea rechazar el cambio bancario de ${request.store_email}?`}
						</DialogDescription>
					</DialogHeader>
					<Input
						containerClassName="w-full"
						value={notes}
						onChange={onNotesChange}
						placeholder="Notas (opcional)"
						floatingLabel="Notas"
					/>
					<DialogFooter>
						<Button variant="secondary" onClick={() => setConfirmAction(null)}>
							Cancelar
						</Button>
						<Button
							variant={confirmAction === 'approved' ? 'primary' : 'secondary'}
							className={confirmAction === 'denied' ? '!border-red-500/20 !text-red-400 hover:!bg-red-500/10' : ''}
							onClick={() => {
								if (confirmAction === 'approved') onApprove();
								else onDeny();
								setConfirmAction(null);
							}}
						>
							{confirmAction === 'approved' ? 'Aprobar' : 'Rechazar'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
